<template>
  <div>
    <div class="mb-6">
      <h2 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">Email-Templates</h2>
      <p class="text-gray-600 dark:text-gray-400">Verwalten Sie die Email-Templates für Anfragen und Bestätigungen</p>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center items-center py-12">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400"></div>
    </div>

    <!-- Templates List -->
    <div v-else class="space-y-6">
      <!-- Admin Notification Template -->
      <div class="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
        <div class="flex items-center justify-between mb-4">
          <div>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
              1. Admin - Information (Private E-Mail)
            </h3>
            <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Einfache Benachrichtigung an deine private E-Mail-Adresse
            </p>
          </div>
          <div class="flex items-center gap-2">
            <span
              :class="[
                'px-3 py-1 rounded-full text-xs font-medium',
                adminNotificationTemplate?.isActive
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
              ]"
            >
              {{ adminNotificationTemplate?.isActive ? 'Aktiv' : 'Inaktiv' }}
            </span>
            <button
              @click="editTemplate('admin-notification')"
              class="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors text-sm font-medium"
            >
              Bearbeiten
            </button>
          </div>
        </div>
      </div>

      <!-- Inquiry Template -->
      <div class="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
        <div class="flex items-center justify-between mb-4">
          <div>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
              2. Anfrage-Template (Admin E-Mail)
            </h3>
            <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Ausführliche Anfrage-Details an deine Admin E-Mail (Strato)
            </p>
          </div>
          <div class="flex items-center gap-2">
            <span
              :class="[
                'px-3 py-1 rounded-full text-xs font-medium',
                inquiryTemplate?.isActive
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
              ]"
            >
              {{ inquiryTemplate?.isActive ? 'Aktiv' : 'Inaktiv' }}
            </span>
            <button
              @click="editTemplate('inquiry')"
              class="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors text-sm font-medium"
            >
              Bearbeiten
            </button>
          </div>
        </div>
      </div>

      <!-- Confirmation Template -->
      <div class="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
        <div class="flex items-center justify-between mb-4">
          <div>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
              3. Bestätigungs-Template (an Nutzer)
            </h3>
            <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Wird an den Nutzer gesendet, um die Anfrage zu bestätigen
            </p>
          </div>
          <div class="flex items-center gap-2">
            <span
              :class="[
                'px-3 py-1 rounded-full text-xs font-medium',
                confirmationTemplate?.isActive
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
              ]"
            >
              {{ confirmationTemplate?.isActive ? 'Aktiv' : 'Inaktiv' }}
            </span>
            <button
              @click="editTemplate('confirmation')"
              class="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors text-sm font-medium"
            >
              Bearbeiten
            </button>
          </div>
        </div>
      </div>

      <!-- Reply Template -->
      <div class="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
        <div class="flex items-center justify-between mb-4">
          <div>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
              4. Antwort-Template (an Kunden)
            </h3>
            <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Template für manuelle Antworten an Kunden
            </p>
          </div>
          <div class="flex items-center gap-2">
            <span
              :class="[
                'px-3 py-1 rounded-full text-xs font-medium',
                replyTemplate?.isActive
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
              ]"
            >
              {{ replyTemplate?.isActive ? 'Aktiv' : 'Inaktiv' }}
            </span>
            <button
              @click="editTemplate('reply')"
              class="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors text-sm font-medium"
            >
              Bearbeiten
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Modal -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition-opacity duration-300"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-200"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="editingTemplate"
          class="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4 overflow-y-auto"
          @click.self="closeEditModal"
        >
          <!-- Backdrop -->
          <div
            class="absolute inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 transition-opacity"
            @click="closeEditModal"
          ></div>

          <!-- Modal Content -->
          <Transition
            enter-active-class="transition-all duration-300"
            enter-from-class="opacity-0 scale-95 translate-y-4"
            enter-to-class="opacity-100 scale-100 translate-y-0"
            leave-active-class="transition-all duration-200"
            leave-from-class="opacity-100 scale-100 translate-y-0"
            leave-to-class="opacity-0 scale-95 translate-y-4"
          >
            <div
              v-if="editingTemplate"
              class="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-4xl w-full border border-gray-200 dark:border-gray-700 transition-all duration-300 overflow-hidden flex flex-col max-h-[90vh]"
              role="dialog"
              aria-modal="true"
              @click.stop
            >
              <!-- Header -->
              <div class="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                <h3 class="text-xl font-bold text-gray-900 dark:text-white">
                  {{ editingTemplate.type === 'inquiry' ? 'Anfrage-Template' : editingTemplate.type === 'confirmation' ? 'Bestätigungs-Template' : 'Antwort-Template' }} bearbeiten
                </h3>
                <button
                  @click="closeEditModal"
                  class="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                  aria-label="Schließen"
                >
                  <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <!-- Body -->
              <div class="p-6 overflow-y-auto flex-1 min-h-0">
        <div class="space-y-6">
          <!-- Template Name -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Template-Name
            </label>
            <input
              v-model="editForm.name"
              type="text"
              class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
              placeholder="Template-Name"
            />
          </div>

          <!-- Subject -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Betreff
            </label>
            <input
              v-model="editForm.subject"
              type="text"
              class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
              placeholder="Email-Betreff"
            />
            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400" v-pre>
              Verfügbare Variablen: {{name}}, {{email}}, {{interest}}, {{projectType}}, {{budget}}, {{date}}, {{message}}, {{nextSteps}}
            </p>
          </div>

          <!-- HTML Content -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              HTML-Inhalt
            </label>
            <textarea
              v-model="editForm.htmlContent"
              rows="20"
              class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
              placeholder="HTML-Inhalt des Templates..."
            ></textarea>
            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400" v-pre>
              Verfügbare Variablen: {{name}}, {{email}}, {{interest}}, {{interestLabel}}, {{projectType}}, {{projectTypeLabel}}, {{budget}}, {{budgetLabel}}, {{message}}, {{date}}, {{nextSteps}}
            </p>
          </div>

          <!-- Active Toggle -->
          <div class="flex items-center">
            <input
              v-model="editForm.isActive"
              type="checkbox"
              id="isActive"
              class="h-4 w-4 text-blue-600 dark:text-blue-400 focus:ring-blue-500 dark:focus:ring-blue-400 border-gray-300 dark:border-gray-600 rounded"
            />
            <label for="isActive" class="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Template aktivieren
            </label>
          </div>

          <!-- Preview Button -->
          <div class="flex justify-between items-center pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              @click="showPreview = !showPreview"
              class="px-4 py-2 bg-gray-600 dark:bg-gray-700 text-white rounded-lg hover:bg-gray-700 dark:hover:bg-gray-600 transition-colors text-sm font-medium"
            >
              {{ showPreview ? 'Vorschau ausblenden' : 'Vorschau anzeigen' }}
            </button>
            <div class="flex gap-2">
              <button
                @click="loadDefaultTemplate"
                class="px-4 py-2 bg-gray-600 dark:bg-gray-700 text-white rounded-lg hover:bg-gray-700 dark:hover:bg-gray-600 transition-colors text-sm font-medium"
              >
                Standard-Template laden
              </button>
            </div>
          </div>

          <!-- Preview -->
          <div v-if="showPreview" class="border border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-white dark:bg-gray-800">
            <h4 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Vorschau:</h4>
            <div class="border border-gray-200 dark:border-gray-700 rounded p-4 bg-gray-50 dark:bg-gray-900">
              <div v-html="previewContent"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
              <div class="flex justify-end gap-2 p-6 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
                <button
                  @click="closeEditModal"
                  class="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Abbrechen
                </button>
                <button
                  @click="saveTemplate"
                  :disabled="saving"
                  class="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {{ saving ? 'Wird gespeichert...' : 'Speichern' }}
                </button>
              </div>
            </div>
          </Transition>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import {
  getEmailTemplates,
  saveEmailTemplate,
  createDefaultTemplates,
  type EmailTemplate
} from '../../firebase/emailTemplates'
import { useErrorHandler } from '../../composables/useErrorHandler'

const loading = ref(true)
const saving = ref(false)
const showPreview = ref(false)
const templates = ref<EmailTemplate[]>([])
const editingTemplate = ref<EmailTemplate | null>(null)
const { handleError } = useErrorHandler()

const editForm = ref({
  name: '',
  subject: '',
  htmlContent: '',
  isActive: true
})

const adminNotificationTemplate = computed(() => 
  templates.value.find(t => t.type === 'admin-notification' && t.isActive) || 
  templates.value.find(t => t.type === 'admin-notification')
)

const inquiryTemplate = computed(() => 
  templates.value.find(t => t.type === 'inquiry' && t.isActive) || 
  templates.value.find(t => t.type === 'inquiry')
)

const confirmationTemplate = computed(() => 
  templates.value.find(t => t.type === 'confirmation' && t.isActive) || 
  templates.value.find(t => t.type === 'confirmation')
)

const replyTemplate = computed(() => 
  templates.value.find(t => t.type === 'reply' && t.isActive) || 
  templates.value.find(t => t.type === 'reply')
)

const previewContent = computed(() => {
  if (!editForm.value.htmlContent) return ''
  
  let html = editForm.value.htmlContent
  
  // Beispielwerte für die Vorschau
  const exampleInterestLabel = 'Webentwicklung'
  const exampleInterest = 'webdev'
  const exampleProjectTypeLabel = 'Neues Projekt starten'
  const exampleBudgetLabel = '5.000 € - 10.000 €'
  const exampleProjectType = 'new'
  const exampleBudget = '5k-10k'
  
  // Verarbeite bedingte Blöcke ZUERST ({{#if ...}}...{{/if}})
  html = html.replace(/\{\{#if\s+(\w+)\}\}([\s\S]*?)\{\{\/if\}\}/g, (_match, condition, content) => {
    // Prüfe ob der Wert existiert (für Vorschau immer true, damit Beispielwerte angezeigt werden)
    let value
    switch (condition) {
      case 'interestLabel':
        value = exampleInterestLabel
        break
      case 'interest':
        value = exampleInterest
        break
      case 'projectTypeLabel':
        value = exampleProjectTypeLabel
        break
      case 'budgetLabel':
        value = exampleBudgetLabel
        break
      case 'projectType':
        value = exampleProjectType
        break
      case 'budget':
        value = exampleBudget
        break
      default:
        // Für andere Bedingungen, prüfe ob Beispielwert existiert
        value = null
    }
    const hasValue = value && value !== '' && value !== 'undefined'
    return hasValue ? content : ''
  })
  
  // Ersetze Variablen mit Beispielwerten NACH der bedingten Verarbeitung
  html = html
    .replace(/\{\{name\}\}/g, 'Max Mustermann')
    .replace(/\{\{email\}\}/g, 'max@example.com')
    .replace(/\{\{interest\}\}/g, exampleInterest)
    .replace(/\{\{interestLabel\}\}/g, exampleInterestLabel)
    .replace(/\{\{projectType\}\}/g, exampleProjectType)
    .replace(/\{\{projectTypeLabel\}\}/g, exampleProjectTypeLabel)
    .replace(/\{\{budget\}\}/g, exampleBudget)
    .replace(/\{\{budgetLabel\}\}/g, exampleBudgetLabel)
    .replace(/\{\{message\}\}/g, 'Ich interessiere mich für eine neue Website...')
    .replace(/\{\{date\}\}/g, new Date().toLocaleDateString('de-DE'))
    .replace(/\{\{nextSteps\}\}/g, 'Ich werde mich in Kürze mit Ihnen in Verbindung setzen.')
  
  return html
})

const loadTemplates = async () => {
  try {
    loading.value = true
    
    // Erstelle fehlende Standard-Templates (inkl. Reply-Template falls es noch nicht existiert)
    await createDefaultTemplates()
    
    // Lade Templates erneut, um sicherzustellen, dass alle vorhanden sind
    templates.value = await getEmailTemplates()
  } catch (error) {
    handleError(error, 'Fehler beim Laden der Templates')
  } finally {
    loading.value = false
  }
}

const editTemplate = (type: 'inquiry' | 'confirmation' | 'reply' | 'admin-notification') => {
  const template = type === 'admin-notification'
    ? adminNotificationTemplate.value
    : type === 'inquiry' 
    ? inquiryTemplate.value 
    : type === 'confirmation' 
    ? confirmationTemplate.value 
    : replyTemplate.value
  
  if (template) {
    editingTemplate.value = template
    editForm.value = {
      name: template.name,
      subject: template.subject,
      htmlContent: template.htmlContent,
      isActive: template.isActive
    }
  } else {
    // Erstelle neues Template
    const templateNames = {
      'admin-notification': 'Admin - Information (Private E-Mail)',
      inquiry: 'Anfrage-Template (Admin E-Mail)',
      confirmation: 'Bestätigungs-Template',
      reply: 'Antwort-Template'
    }
    const templateSubjects = {
      'admin-notification': 'Neue Kontaktanfrage von {{name}}',
      inquiry: 'Neue Kontaktanfrage von {{name}}',
      confirmation: 'Vielen Dank für Ihre Anfrage',
      reply: 'Antwort auf Ihre Anfrage'
    }
    
    // Lade Standard-HTML-Inhalt basierend auf Typ
    let defaultHtmlContent = ''
    if (type === 'inquiry') {
      defaultHtmlContent = getDefaultInquiryTemplate()
    } else if (type === 'confirmation') {
      defaultHtmlContent = getDefaultConfirmationTemplate()
    } else {
      defaultHtmlContent = getDefaultReplyTemplate()
    }
    
    editingTemplate.value = {
      id: '',
      name: templateNames[type],
      type,
      subject: templateSubjects[type],
      htmlContent: defaultHtmlContent,
      isActive: true,
      createdAt: new Date(),
      updatedAt: new Date()
    }
    editForm.value = {
      name: editingTemplate.value.name,
      subject: editingTemplate.value.subject,
      htmlContent: editingTemplate.value.htmlContent,
      isActive: editingTemplate.value.isActive
    }
  }
}

const closeEditModal = () => {
  editingTemplate.value = null
  showPreview.value = false
}

const saveTemplate = async () => {
  if (!editingTemplate.value) return
  
  try {
    saving.value = true
    
    const templateData: Partial<EmailTemplate> & { name: string; type: 'inquiry' | 'confirmation' | 'reply' | 'admin-notification' } = {
      id: editingTemplate.value.id,
      name: editForm.value.name,
      type: editingTemplate.value.type,
      subject: editForm.value.subject,
      htmlContent: editForm.value.htmlContent,
      isActive: editForm.value.isActive
    }
    
    await saveEmailTemplate(templateData)
    
    // Lade Templates neu
    await loadTemplates()
    
    closeEditModal()
  } catch (error) {
    handleError(error, 'Fehler beim Speichern des Templates')
  } finally {
    saving.value = false
  }
}

const loadDefaultTemplate = () => {
  if (!editingTemplate.value) return
  
  // Lade Standard-Template basierend auf Typ
  if (editingTemplate.value.type === 'inquiry') {
    editForm.value.htmlContent = getDefaultInquiryTemplate()
  } else if (editingTemplate.value.type === 'confirmation') {
    editForm.value.htmlContent = getDefaultConfirmationTemplate()
  } else {
    editForm.value.htmlContent = getDefaultReplyTemplate()
  }
}

const getDefaultInquiryTemplate = (): string => {
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Neue Kontaktanfrage</title>
</head>
<body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; line-height: 1.6; color: #111827; background-color: #ffffff; max-width: 600px; margin: 0 auto; padding: 20px;">
  <div style="background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%); padding: 40px; text-align: center; border-radius: 10px 10px 0 0;">
    <div style="background-color: rgba(255,255,255,0.95); width: 100px; height: 100px; border-radius: 50%; margin: 0 auto 20px; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 12px rgba(0,0,0,0.15); border: 3px solid rgba(255,255,255,0.5);">
      <span style="font-size: 56px; color: #3b82f6; font-weight: bold; line-height: 1; display: block;">📧</span>
    </div>
    <h1 style="color: white; margin: 0; font-size: 28px; font-weight: 700;">Neue Kontaktanfrage</h1>
    <p style="color: rgba(255,255,255,0.9); margin: 10px 0 0; font-size: 16px;">Sie haben eine neue Nachricht erhalten</p>
  </div>
  
  <div style="background-color: #f9fafb; padding: 30px; border-radius: 0 0 10px 10px; border: 1px solid #e5e7eb; border-top: none;">
    <div style="background-color: #ffffff; padding: 25px; border-radius: 8px; margin-bottom: 20px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); border: 1px solid #e5e7eb;">
      <p style="color: #374151; font-size: 16px; line-height: 1.8; margin: 0 0 20px;">
        Sie haben eine neue Kontaktanfrage von <strong style="color: #111827;">{{name}}</strong> erhalten.
      </p>
    </div>
    
    <div style="background-color: #ffffff; padding: 20px; border-radius: 8px; margin-bottom: 20px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); border: 1px solid #e5e7eb;">
      <h2 style="color: #111827; font-size: 18px; font-weight: 600; margin: 0 0 15px;">Kontaktdaten:</h2>
      
      <div style="margin-bottom: 15px; padding-bottom: 15px; border-bottom: 1px solid #e5e7eb;">
        <div style="font-weight: 600; color: #6b7280; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 5px;">Name</div>
        <div style="font-size: 16px; color: #111827;">{{name}}</div>
      </div>
      
      <div style="margin-bottom: 15px; padding-bottom: 15px; border-bottom: 1px solid #e5e7eb;">
        <div style="font-weight: 600; color: #6b7280; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 5px;">E-Mail</div>
        <div style="font-size: 16px; color: #111827;"><a href="mailto:{{email}}" style="color: #3b82f6; text-decoration: none;">{{email}}</a></div>
      </div>
      
      {{#if interestLabel}}
      <div style="margin-bottom: 15px; padding-bottom: 15px; border-bottom: 1px solid #e5e7eb;">
        <div style="font-weight: 600; color: #6b7280; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 5px;">Interessensgebiet</div>
        <div style="font-size: 16px; color: #111827;">{{interestLabel}}</div>
      </div>
      {{/if}}
      
      {{#if projectTypeLabel}}
      <div style="margin-bottom: 15px; padding-bottom: 15px; border-bottom: 1px solid #e5e7eb;">
        <div style="font-weight: 600; color: #6b7280; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 5px;">Projekttyp</div>
        <div style="font-size: 16px; color: #111827;">{{projectTypeLabel}}</div>
      </div>
      {{/if}}
      
      {{#if budgetLabel}}
      <div style="margin-bottom: 15px; padding-bottom: 15px; border-bottom: 1px solid #e5e7eb;">
        <div style="font-weight: 600; color: #6b7280; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 5px;">Budget</div>
        <div style="font-size: 16px; color: #111827;">{{budgetLabel}}</div>
      </div>
      {{/if}}
      
      <div style="margin-top: 15px;">
        <div style="font-weight: 600; color: #6b7280; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 5px;">Nachricht</div>
        <div style="color: #374151; line-height: 1.8; white-space: pre-wrap; background-color: #f9fafb; padding: 15px; border-radius: 6px;">{{message}}</div>
      </div>
    </div>
    
    <div style="background-color: #eff6ff; border-left: 4px solid #3b82f6; padding: 15px; border-radius: 6px; margin-bottom: 20px;">
      <p style="color: #1e40af; font-size: 14px; margin: 0; line-height: 1.6;">
        <strong>Nächste Schritte:</strong> Bitte antworten Sie dem Kunden innerhalb von 24 Stunden. Die Kontaktdaten finden Sie oben in dieser E-Mail.
      </p>
    </div>
    
    <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #e5e7eb; text-align: center; color: #6b7280; font-size: 12px;">
      <p style="margin: 0;">Gesendet am: {{date}}</p>
    </div>
  </div>
</body>
</html>`
}

const getDefaultConfirmationTemplate = (): string => {
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Vielen Dank für Ihre Anfrage</title>
</head>
<body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
  <div style="background: linear-gradient(135deg, #10b981 0%, #059669 100%); padding: 40px; text-align: center; border-radius: 10px 10px 0 0;">
    <div style="background: white; width: 80px; height: 80px; border-radius: 50%; margin: 0 auto 20px; display: flex; align-items: center; justify-content: center;">
      <span style="font-size: 40px; color: #10b981;">✓</span>
    </div>
    <h1 style="color: white; margin: 0; font-size: 28px; font-weight: 700;">Vielen Dank!</h1>
    <p style="color: rgba(255,255,255,0.9); margin: 10px 0 0; font-size: 16px;">Ihre Anfrage wurde erfolgreich übermittelt</p>
  </div>
  
  <div style="background: #f9fafb; padding: 30px; border-radius: 0 0 10px 10px; border: 1px solid #e5e7eb; border-top: none;">
    <div style="background: white; padding: 25px; border-radius: 8px; margin-bottom: 20px; box-shadow: 0 1px 3px rgba(0,0,0,0.1);">
      <p style="color: #374151; font-size: 16px; line-height: 1.8; margin: 0 0 20px;">
        Hallo <strong>{{name}}</strong>,
      </p>
      <p style="color: #374151; font-size: 16px; line-height: 1.8; margin: 0 0 20px;">
        vielen Dank für Ihre Kontaktanfrage! Ich habe Ihre Nachricht erhalten und werde mich schnellstmöglich bei Ihnen melden.
      </p>
    </div>
    
    <div style="background: white; padding: 20px; border-radius: 8px; margin-bottom: 20px; box-shadow: 0 1px 3px rgba(0,0,0,0.1);">
      <h2 style="color: #111827; font-size: 18px; font-weight: 600; margin: 0 0 15px;">Ihre Anfrage im Überblick:</h2>
      
      {{#if interestLabel}}
      <div style="margin-bottom: 15px; padding-bottom: 15px; border-bottom: 1px solid #e5e7eb;">
        <div style="font-weight: 600; color: #6b7280; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 5px;">Interessensgebiet</div>
        <div style="font-size: 16px; color: #111827;">{{interestLabel}}</div>
      </div>
      {{/if}}
      
      {{#if projectTypeLabel}}
      <div style="margin-bottom: 15px; padding-bottom: 15px; border-bottom: 1px solid #e5e7eb;">
        <div style="font-weight: 600; color: #6b7280; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 5px;">Projekttyp</div>
        <div style="font-size: 16px; color: #111827;">{{projectTypeLabel}}</div>
      </div>
      {{/if}}
      
      {{#if budgetLabel}}
      <div style="margin-bottom: 15px; padding-bottom: 15px; border-bottom: 1px solid #e5e7eb;">
        <div style="font-weight: 600; color: #6b7280; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 5px;">Budget</div>
        <div style="font-size: 16px; color: #111827;">{{budgetLabel}}</div>
      </div>
      {{/if}}
      
      <div style="margin-top: 15px;">
        <div style="font-weight: 600; color: #6b7280; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 5px;">Ihre Nachricht</div>
        <div style="color: #374151; line-height: 1.8; white-space: pre-wrap; background: #f9fafb; padding: 15px; border-radius: 6px;">{{message}}</div>
      </div>
    </div>
    
    <div style="background: #eff6ff; border-left: 4px solid #3b82f6; padding: 15px; border-radius: 6px; margin-bottom: 20px;">
      <p style="color: #1e40af; font-size: 14px; margin: 0; line-height: 1.6;">
        <strong>Nächste Schritte:</strong> Ich werde Ihre Anfrage prüfen und mich innerhalb von 24 Stunden bei Ihnen melden. Bei dringenden Anliegen können Sie mich auch direkt kontaktieren.
      </p>
    </div>
    
    <div style="text-align: center; padding-top: 20px; border-top: 1px solid #e5e7eb; color: #6b7280; font-size: 14px;">
      <p style="margin: 0 0 10px;">Mit freundlichen Grüßen</p>
      <p style="margin: 0; font-weight: 600; color: #111827;">MAXIMILIAN REITSBERGER</p>
      <p style="margin: 5px 0 0; font-size: 12px;">Webentwickler mit QA-Expertise</p>
    </div>
  </div>
</body>
</html>`
}

const getDefaultReplyTemplate = (): string => {
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta name="color-scheme" content="light dark">
  <meta name="supported-color-schemes" content="light dark">
  <title>Antwort auf Ihre Anfrage</title>
  <style>
    @media (prefers-color-scheme: dark) {
      .email-container {
        background-color: #1f2937 !important;
        color: #f3f4f6 !important;
      }
      .email-card {
        background-color: #1f2937 !important;
        border-color: #374151 !important;
      }
      .email-text-primary {
        color: #f9fafb !important;
      }
      .email-text-secondary {
        color: #d1d5db !important;
      }
      .email-message-box {
        background-color: #1f2937 !important;
      }
      .email-info-box {
        background-color: #1e3a5f !important;
        border-color: #3b82f6 !important;
      }
      .email-info-text {
        color: #93c5fd !important;
      }
      .email-link {
        color: #60a5fa !important;
      }
    }
  </style>
</head>
<body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; line-height: 1.6; color: #111827; background-color: #ffffff; max-width: 600px; margin: 0 auto; padding: 20px;">
  <div style="background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%); padding: 40px; text-align: center; border-radius: 10px 10px 0 0;">
    <div style="background-color: rgba(255,255,255,0.95); width: 100px; height: 100px; border-radius: 50%; margin: 0 auto 20px; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 12px rgba(0,0,0,0.15); border: 3px solid rgba(255,255,255,0.5);">
      <span style="font-size: 56px; color: #6366f1; font-weight: bold; line-height: 1; display: block;">✉️</span>
    </div>
    <h1 style="color: white; margin: 0; font-size: 28px; font-weight: 700;">Antwort auf Ihre Anfrage</h1>
    <p style="color: rgba(255,255,255,0.9); margin: 10px 0 0; font-size: 16px;">Vielen Dank für Ihr Interesse</p>
  </div>
  
  <div class="email-container" style="background-color: #f9fafb; padding: 30px; border-radius: 0 0 10px 10px; border: 1px solid #e5e7eb; border-top: none;">
    <div class="email-card" style="background-color: #ffffff; padding: 25px; border-radius: 8px; margin-bottom: 20px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); border: 1px solid #e5e7eb;">
      <p class="email-text-secondary" style="color: #374151; font-size: 16px; line-height: 1.8; margin: 0 0 20px;">
        Hallo <strong class="email-text-primary" style="color: #111827;">{{name}}</strong>,
      </p>
      <p class="email-text-secondary" style="color: #374151; font-size: 16px; line-height: 1.8; margin: 0 0 20px;">
        vielen Dank für Ihre Kontaktanfrage vom {{date}}. Ich habe Ihre Nachricht erhalten und möchte Ihnen gerne antworten.
      </p>
    </div>
    
    <div class="email-card" style="background-color: #ffffff; padding: 20px; border-radius: 8px; margin-bottom: 20px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); border: 1px solid #e5e7eb;">
      <div class="email-message-box email-text-secondary" style="color: #374151; line-height: 1.8; white-space: pre-wrap; background-color: #f9fafb; padding: 20px; border-radius: 6px; font-size: 16px;">{{message}}</div>
    </div>
    
    {{#if nextSteps}}
    <div class="email-info-box" style="background-color: #eff6ff; border-left: 4px solid #3b82f6; padding: 15px; border-radius: 6px; margin-bottom: 20px;">
      <p class="email-info-text" style="color: #1e40af; font-size: 14px; margin: 0; line-height: 1.6;">
        <strong>Nächste Schritte:</strong> {{nextSteps}}
      </p>
    </div>
    {{/if}}
    
    <div class="email-signature" style="text-align: center; padding-top: 20px; border-top: 1px solid #e5e7eb; color: #6b7280; font-size: 14px;">
      <p style="margin: 0 0 10px;">Mit freundlichen Grüßen</p>
      <p class="email-text-primary" style="margin: 0; font-weight: 600; color: #111827;">MAXIMILIAN REITSBERGER</p>
      <p style="margin: 5px 0 0; font-size: 12px;">Webentwickler mit QA-Expertise</p>
      <p style="margin: 10px 0 0; font-size: 12px;">
        <a href="mailto:kontakt@reitsberger.de" class="email-link" style="color: #3b82f6; text-decoration: none;">kontakt@reitsberger.de</a>
      </p>
    </div>
  </div>
</body>
</html>`
}

onMounted(() => {
  loadTemplates()
})
</script>
